// The display each annotator left on each frame, so that reopening a frame shows it the way it
// was set there -- not the way the previous frame, the previous job or a fresh Auto would have it.
//
// The window used to be one value for the whole tab: stepping to another frame carried it along,
// opening another job carried it along (clamped to that job's range on the way), and a reload
// replaced it with Auto of whichever frame happened to open first. The same frame could therefore
// come back looking three different ways. Kept in localStorage, per user and job, because the
// server has no place for it; it follows the browser, not the account.

import { getCVATStore } from 'cvat-store';
import type { DisplayCurve } from 'utils/sixteen-bit';

export interface FrameDisplay {
    // In SOURCE units, as everywhere else: camera counts on a 16-bit frame.
    min: number;
    max: number;
    curve: DisplayCurve;
    gammaLevel: number;
    asinhSoftness: number;
}

const CURVES: DisplayCurve[] = ['linear', 'gamma', 'asinh'];

function storageKey(jobID: number): string {
    const username = getCVATStore().getState().auth.user?.username ?? '';
    return `cvatFrameDisplay:${username}:${jobID}`;
}

// Read afresh every time rather than cached: the same job open in two tabs would otherwise have
// each tab write back its own stale copy of every other frame.
function readJob(jobID: number): Record<string, FrameDisplay> {
    try {
        const parsed = JSON.parse(localStorage.getItem(storageKey(jobID)) || '{}');
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
}

export function loadFrameDisplay(jobID: number, frame: number): FrameDisplay | null {
    const stored = readJob(jobID)[frame];
    if (!stored) return null;
    const {
        min, max, curve, gammaLevel, asinhSoftness,
    } = stored;
    const valid = [min, max, gammaLevel, asinhSoftness].every(Number.isFinite) &&
        max > min && CURVES.includes(curve);
    return valid ? {
        min, max, curve, gammaLevel, asinhSoftness,
    } : null;
}

export function saveFrameDisplay(jobID: number, frame: number, display: FrameDisplay): void {
    try {
        const job = readJob(jobID);
        job[frame] = display;
        localStorage.setItem(storageKey(jobID), JSON.stringify(job));
    } catch {
        // Storage full or disabled: the frame simply is not remembered.
    }
}
